import React, { useState, useEffect, useCallback } from 'react';
import { testimonials } from '../data';
import { Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const totalItems = testimonials.length;

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % totalItems);
  }, [totalItems]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + totalItems) % totalItems);
  }, [totalItems]);

  useEffect(() => {
    const interval = setInterval(nextSlide, 8000); // Slightly slower for reading 2 cards
    return () => clearInterval(interval);
  }, [nextSlide]);

  return (
    <section id="anmeldelser" className="py-24 md:py-32 bg-stone-100/60 overflow-hidden border-y border-stone-200">
      <div className="container mx-auto px-4 md:px-12">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center mb-16"
        >
          <span className="text-[10px] uppercase tracking-[0.3em] text-stone-400 mb-6 block font-bold">
            Klienternes oplevelse
          </span>
          <h2 className="text-4xl md:text-5xl font-light text-stone-900 leading-tight">
            Det siger <span className="font-serif italic text-stone-500 lowercase tracking-normal">mine klienter</span>
          </h2>
        </motion.div>

        <div className="max-w-6xl mx-auto relative group px-2 md:px-0">
          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute -left-2 md:-left-16 top-1/2 -translate-y-1/2 z-10 p-2 md:p-3 rounded-full bg-white/90 shadow-sm border border-stone-200 text-stone-400 hover:text-stone-900 hover:border-stone-400 transition-all focus:outline-none hidden sm:block"
            aria-label="Forrige anmeldelse"
          >
            <ChevronLeft size={20} className="md:w-6 md:h-6" />
          </button>

          <button
            onClick={nextSlide}
            className="absolute -right-2 md:-right-16 top-1/2 -translate-y-1/2 z-10 p-2 md:p-3 rounded-full bg-white/90 shadow-sm border border-stone-200 text-stone-400 hover:text-stone-900 hover:border-stone-400 transition-all focus:outline-none hidden sm:block"
            aria-label="Næste anmeldelse"
          >
            <ChevronRight size={20} className="md:w-6 md:h-6" />
          </button>

          {/* Carousel Container */}
          <div className="overflow-hidden">
            <motion.div 
              className="flex"
              animate={{ 
                x: `-${currentIndex * (100 / (isMobile ? 1 : 2))}%` 
              }}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 30
              }}
            >
              {[...testimonials, ...testimonials].map((testimonial, index) => (
                <div 
                  key={index} 
                  className="w-full md:w-1/2 flex-shrink-0 px-2"
                >
                  <div className="bg-white p-10 md:p-14 border border-stone-200 flex flex-col justify-between h-full hover:shadow-md transition-all duration-500">
                    <div>
                      <Quote className="text-stone-200 mb-8 w-10 h-10" />
                      <p className="text-lg md:text-xl text-stone-700 font-light italic leading-relaxed mb-10">
                        "{testimonial.text}"
                      </p>
                    </div>
                    <div className="border-t border-stone-100 pt-8">
                      <p className="text-base font-medium text-stone-900 tracking-wide uppercase">{testimonial.name}</p>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-stone-400 mt-2">{testimonial.location}</p>
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Mobile Dot Navigation */}
          <div className="flex justify-center space-x-2 mt-6 sm:hidden">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  currentIndex % totalItems === idx ? 'bg-stone-400 w-4' : 'bg-stone-200'
                }`}
                aria-label={`Gå til anmeldelse ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
